import Dialog from 'carbon-react/lib/components/dialog';
import Form from 'carbon-react/lib/components/form';
import React, { FC, useCallback, useEffect, useMemo, useRef } from 'react';
import Button from 'carbon-react/lib/components/button';

import { FormattedMessage, useIntl } from '@futrli/localisation';
import { UILoader } from '@futrli/ui-components';

import { LinkedSheetData } from '../../types';
import { useSheetsSyncStatus } from '../../../hooks/use-sheets-sync-status/use-sheets-sync-status';
import { SyncTemplateList } from '../sync-template-list/sync-template-list';
import { useSheetsSync } from '../../../hooks/use-sheets-sync/use-sheets-sync';

// import styles from './sync-modal.scss';

export interface Props {
  data: LinkedSheetData[];
  onClose?: (refreshNeeded: boolean) => void;
}

export const SyncAllModal: FC<Props> = ({ data, onClose }) => {
  const { results, isLoading } = useSheetsSyncStatus(data);
  const { busy: syncingIds, results: syncResults, syncAll } = useSheetsSync();

  const intl = useIntl();

  const readySheets = useMemo(() => {
    return data.filter(item => !isLoading && results[item.forecastId]?.allowImport && !results[item.forecastId]?.error);
  }, [data, isLoading, results]);
  const notReadySheets = useMemo(() => {
    return data.filter(
      item => !isLoading && (!results[item.forecastId]?.allowImport || results[item.forecastId]?.error)
    );
  }, [data, isLoading, results]);

  const allowImport = readySheets.length > 0;
  const noSheets = !readySheets.length && !notReadySheets.length;
  const refreshNeeded = useRef(false);

  const handleClose = useCallback(() => {
    if (!syncingIds.length) {
      onClose?.(refreshNeeded.current);
    }
  }, [onClose, syncingIds.length]);

  const handleSync = useCallback(async () => {
    await syncAll(readySheets);
  }, [readySheets, syncAll]);

  const getContent = () => {
    if (noSheets) {
      return (
        <p>
          <FormattedMessage
            id="import.no_sheets_to_sync"
            defaultMessage="There are no forecasts that are ready to be synced."
          />
        </p>
      );
    } else {
      return (
        <>
          {readySheets.length > 0 && (
            <>
              <p>
                <FormattedMessage
                  id="import.data_will_be_imported_entities"
                  defaultMessage="Data will be imported for these entities:"
                />
              </p>
              <SyncTemplateList sheets={readySheets} syncingIds={syncingIds} syncResults={syncResults} />
            </>
          )}
          {notReadySheets.length > 0 && (
            <>
              <p style={{ marginTop: readySheets.length ? '30px' : undefined }}>
                <FormattedMessage
                  id="import.entities_cannot_currently_be_synced"
                  defaultMessage="The following entities cannot currently be synced:"
                />
              </p>
              <SyncTemplateList sheets={notReadySheets} syncStatusResults={results} />
            </>
          )}
        </>
      );
    }
  };

  // Keep watch of the sync results. If at any point a sync succeeds, set refreshNeeded flag to true.
  useEffect(() => {
    if (refreshNeeded.current === false) {
      refreshNeeded.current = !!Object.entries(syncResults).find(([, result]) => result === true);
    }
  }, [syncResults]);

  return (
    <Dialog
      open={true}
      onCancel={handleClose}
      size="medium-small"
      data-testid="sync-dialog"
      aria-label={intl.formatMessage({ id: 'common.sync_status', defaultMessage: 'Sync status' })}
      title={intl.formatMessage({ id: 'common.sync_status', defaultMessage: 'Sync status' })}
    >
      <Form
        stickyFooter
        leftSideButtons={
          !isLoading && (
            <Button
              disabled={isLoading || !!syncingIds.length}
              onClick={handleClose}
              data-testid="sync-dialog-actions-secondary"
            >
              <FormattedMessage id="common.cancel" defaultMessage="Cancel" />
            </Button>
          )
        }
        rightSideButtons={
          !isLoading &&
          allowImport && (
            <Button
              disabled={!allowImport || !!syncingIds.length || noSheets}
              onClick={handleSync}
              buttonType="primary"
              data-testid="sync-dialog-actions-primary"
            >
              <FormattedMessage id="import.sync_predictions" defaultMessage="Sync Predictions" />
            </Button>
          )
        }
        onSubmit={e => e.preventDefault()}
      >
        <div data-testid="sync-dialog-content">
          {isLoading && <UILoader />}
          {!isLoading && getContent()}
        </div>
      </Form>
    </Dialog>
  );
};
